import { CommonModule } from '@angular/common';
import { Component, DestroyRef, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { ThrottleClickDirective } from '@directives/throttle-click.directive';
import { AudioAsset, AudioProject } from '@models/Sessions/Idea/AudioProject';
import { Idea } from '@models/Sessions/Idea/Idea';
import { Session } from '@models/Sessions/Session';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { tap } from 'rxjs';

import { mapAudioClips } from './aduio-project.utils';
import { AudioAssetsComponent } from './audio-assets/audio-assets.component';
import {
  AudioProjectFormControl,
  AudioProjectFormService,
  AudioSegmentFormControl,
  createAssetsControl,
  createControl,
  createEmptySegmentControl,
  mapFormValueToData,
  reCreateControl,
  reCreateSegmentControl,
} from './audio-project.form';
import { AudioProjectService } from './audio-project.service';
import { AudioSegmentComponent } from './audio-segment/audio-segment.component';

@Component({
  selector: 'app-audio-project',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    MessageModule,
    ThrottleClickDirective,
    AudioSegmentComponent,
    AudioAssetsComponent,
  ],
  templateUrl: './audio-project.component.html',
  styleUrl: './audio-project.component.scss',
})
export class AudioProjectComponent implements OnInit {
  @Input() session: Session;
  @Input() idea: Idea;

  form?: FormGroup<AudioProjectFormControl>;
  hasChanges = false;

  get isAssetGenerating() {
    return Object.values(this.form.value.assets).some(
      ({ state }) => state === 'generating',
    );
  }

  get isAnySegmentAssetGenerating() {
    return this.form.value.segments
      .flatMap(({ clips }) => clips.map(({ asset }) => asset.state))
      .some(state => state === 'generating');
  }

  get isAssembleButtonDisable() {
    return (
      this.hasChanges ||
      this.isAssetGenerating ||
      this.isAnySegmentAssetGenerating
    );
  }

  constructor(
    private readonly destroyRef: DestroyRef,
    private readonly confirmationService: ConfirmationService,
    private readonly audioProjectService: AudioProjectService,
    private readonly audioProjectFormService: AudioProjectFormService,
  ) {}

  ngOnInit() {
    this.audioProjectService
      .get({ sessionId: this.session._id, ideaId: this.idea._id })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(({ audioProject }) => {
          this.create(audioProject);
        }),
        tap(() => {
          this.poll();
        }),
      )
      .subscribe();
  }

  private create(audioProject: AudioProject) {
    this.form = new FormGroup<AudioProjectFormControl>(
      createControl(audioProject),
    );
    this.audioProjectFormService
      .setInitialValue(this.form.value)
      .$onChanges(this.form)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([hasChanges]) => {
        this.hasChanges = hasChanges;
      });
  }

  private poll() {
    this.audioProjectService
      .polling({
        sessionId: this.session._id,
        ideaId: this.idea._id,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ audioProject }) => this.onAfterPoll(audioProject));
  }

  private onAfterPoll(audioProject: AudioProject) {
    const clips = mapAudioClips(audioProject);
    this.form.controls.segments.controls
      .flatMap(segment => {
        return segment.controls.clips.controls;
      })
      .forEach(control => {
        const clip = clips[control.value.id];
        if (!clip) {
          return;
        }
        if (clip.asset.state === control.value.asset.state) {
          return;
        }
        control.setControl('asset', new FormControl<AudioAsset>(clip.asset));
        this.audioProjectFormService.setClipInitialValue(control);
      });
    Object.entries(this.form.controls.assets.controls).forEach(
      ([name, control]) => {
        const asset = audioProject.assets[control.value._id];
        if (!asset) {
          return;
        }
        if (asset.state === control.value.state) {
          return;
        }
        this.form.controls.assets.setControl(
          name,
          new FormControl<AudioAsset>(asset),
        );
        this.audioProjectFormService.setAssetInitialValue(control);
      },
    );
  }

  onSegmentAddClick(segment?: FormGroup<AudioSegmentFormControl>) {
    if (!segment) {
      this.form.controls.segments.insert(
        0,
        new FormGroup<AudioSegmentFormControl>(createEmptySegmentControl()),
      );
      return;
    }

    const index = this.findSegmentIndex(segment);

    if (index >= 0) {
      this.form.controls.segments.insert(
        index + 1,
        new FormGroup<AudioSegmentFormControl>(createEmptySegmentControl()),
      );
    }
  }

  onSegmentDeleteClick(segment: FormGroup<AudioSegmentFormControl>) {
    const index = this.findSegmentIndex(segment);
    if (index >= 0) {
      this.form.controls.segments.removeAt(index);
    }
  }

  onSegmentResetClick(segment: FormGroup<AudioSegmentFormControl>) {
    const initialValue =
      this.audioProjectFormService.getSegmentInitialValue(segment);
    if (!initialValue) {
      return;
    }
    const index = this.findSegmentIndex(segment);
    if (index >= 0) {
      this.form.controls.segments.setControl(
        index,
        new FormGroup<AudioSegmentFormControl>(
          reCreateSegmentControl(initialValue),
        ),
      );
    }
  }

  onAssembleClick() {
    this.audioProjectService
      .generateAudio({
        sessionId: this.session._id,
        ideaId: this.idea._id,
      })
      .pipe(
        tap(({ audioProject }) => {
          this.onAfterAssemble(audioProject);
        }),
        tap(() => {
          this.audioProjectFormService.setInitialValue(this.form.value);
        }),
      )
      .subscribe();
  }

  private onAfterAssemble(audioProject: AudioProject) {
    this.form.setControl('assets', createAssetsControl(audioProject.assets));
  }

  onRestartClick(event: Event) {
    this.confirmationService.confirm({
      target: event.target,
      message: `
          This action will re-create Audio Project from scratch! Click 'Cancel' if you changed your mind.
        `,
      icon: 'pi pi-exclamation-circle',
      acceptButtonProps: {
        icon: 'pi pi-check',
        label: 'Confirm',
      },
      rejectButtonProps: {
        icon: 'pi pi-times',
        label: 'Cancel',
        outlined: true,
      },
      accept: () => {
        this.restart();
      },
      reject: () => {
        /** noop */
      },
    });
  }

  private restart() {
    this.audioProjectService
      .upsert({ sessionId: this.session._id, ideaId: this.idea._id })
      .pipe(
        tap(({ audioProject }) => {
          this.onAfterRestart(audioProject);
        }),
        tap(() => {
          this.audioProjectFormService.setInitialValue(this.form.value);
        }),
      )
      .subscribe();
  }

  private onAfterRestart(audioProject: AudioProject) {
    const form = new FormGroup<AudioProjectFormControl>(
      createControl(audioProject),
    );
    for (const name of Object.getOwnPropertyNames(form.controls)) {
      this.form.setControl(name as any, form.get(name));
    }
  }

  onCreateClick() {
    this.audioProjectService
      .upsert({ sessionId: this.session._id, ideaId: this.idea._id })
      .subscribe(({ audioProject }) => {
        this.create(audioProject);
      });
  }

  onSaveClick() {
    this.audioProjectService
      .update(
        {
          sessionId: this.session._id,
          ideaId: this.idea._id,
        },
        mapFormValueToData(this.form.value),
      )
      .pipe(
        tap(({ audioProject }) => {
          this.onAfterSave(audioProject);
        }),
        tap(() => {
          this.audioProjectFormService.setInitialValue(this.form.value);
        }),
      )
      .subscribe();
  }

  private onAfterSave(audioProject: AudioProject) {
    const form = new FormGroup<AudioProjectFormControl>(
      createControl(audioProject),
    );
    for (const name of Object.getOwnPropertyNames(form.controls)) {
      this.form.setControl(name as any, form.get(name));
    }
  }

  onResetClick(event: Event) {
    this.confirmationService.confirm({
      target: event.target,
      message: `
          This action will revert all unsaved changes! Click 'Cancel' if you changed your mind.
        `,
      icon: 'pi pi-exclamation-circle',
      acceptButtonProps: {
        icon: 'pi pi-check',
        label: 'Confirm',
      },
      rejectButtonProps: {
        icon: 'pi pi-times',
        label: 'Cancel',
        outlined: true,
      },
      accept: () => {
        this.reset();
      },
      reject: () => {
        /** noop */
      },
    });
  }

  private reset() {
    const initialValue = this.audioProjectFormService.getInitialValue();
    if (!initialValue) {
      return;
    }
    const form = new FormGroup<AudioProjectFormControl>(
      reCreateControl(initialValue),
    );
    for (const name of Object.getOwnPropertyNames(form.controls)) {
      this.form.setControl(name as any, form.get(name));
    }
  }

  private findSegmentIndex(segment: FormGroup<AudioSegmentFormControl>) {
    return this.form.controls.segments.controls.indexOf(segment);
  }
}
